import React, { useMemo, useState } from 'react';
import { FormFieldType } from '@troia-v3/types';
import type { ChecklistLinkedEntityType, ChecklistResponse, FormAnswer, FormType } from '@troia-v3/types';

import {
  Button,
  ListPageEmpty,
  ListPageLoading,
  StatusPill,
  type SelectOption,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';

import { useChecklistsByEntity } from '../hooks/useChecklists';
import { CHECKLIST_STATUS_META, formatDueRelative } from '../pages/ChecklistsPage';
import { ApplyChecklistTemplateModal } from './ApplyChecklistTemplateModal';

/** Ícone + selo por tipo de modelo — mockup lote-b/13 (`.aic.form`, `.aic.chk`, `.kind`). */
const TEMPLATE_KIND_META: Record<FormType, { icon: 'file-text' | 'check-square'; label: string; bg: string; fg: string; kindBg: string; kindFg: string }> = {
  form: { icon: 'file-text', label: 'Formulário', bg: '#f3e8ff', fg: '#7c3aed', kindBg: '#ede9fe', kindFg: '#6d28d9' },
  checklist: { icon: 'check-square', label: 'Checklist', bg: '#ecfdf5', fg: '#059669', kindBg: '#d1fae5', kindFg: '#047857' },
};

/** Quantos chips de resposta aparecem antes do "+N" (mockup 13-A mostra 5). */
const MAX_ANSWER_CHIPS = 5;

/**
 * Texto de uma resposta para o chip: label da opção quando existe no
 * snapshot do modelo (converte valor bruto tipo "samsung" em rótulo
 * "Samsung"); arrays viram "A, B". Resposta vazia/nula não vira chip.
 */
function answerChip(checklist: ChecklistResponse, answer: FormAnswer): string | null {
  if (
    answer.value === null ||
    answer.value === '' ||
    (Array.isArray(answer.value) && answer.value.length === 0)
  ) {
    return null;
  }
  const field = checklist.templateSnapshot.fields.find((f) => f.id === answer.fieldId);
  const raw = Array.isArray(answer.value) ? answer.value : [answer.value];
  return raw.map((v) => field?.options?.find((o) => o.value === String(v))?.label ?? String(v)).join(', ');
}

/** "dd/MM às HH:mm" — mockup 13-A ("21/08 às 17:41"). */
const formatWhen = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} às ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export interface ChecklistEntityTabProps {
  /** Tipo da entidade dona do vínculo (`linkedEntity.type`). */
  entityType: ChecklistLinkedEntityType;
  entityId: string;
  /**
   * `isOpen` REAL do drawer hospedeiro (lead ou ticket) — gateia todos os
   * fetches desta aba (CLAUDE.md SEMPRE #10). Componente ÚNICO reusado nos
   * dois drawers (feedback `ds_reference_exact_parity`).
   */
  isOpen: boolean;
}

/**
 * ChecklistEntityTab — aba "Checklists" dos drawers de lead (`LeadDetailsDialog`)
 * e ticket (`TicketDetailContent`), Task 17.
 *
 * Lista instâncias de checklist vinculadas via `linkedEntity` (`GET
 * /checklists/by-entity`) + botão "Aplicar modelo" que cria uma nova
 * instância a partir de um modelo `checklist` OU `form` (spec §5.5 — os dois
 * `FormType` são aplicáveis, não só `checklist`).
 *
 * `assigneeName` já vem populado pelo backend (`ChecklistsService.listByEntityForUser`
 * → `withNames`, Two-Phase Fetch). O `useUsersDropdown` client-side fica só
 * como fallback (`checklist.assigneeName ?? userMap.get(checklist.assigneeId)`)
 * para instâncias antigas ou caminhos que ainda não populam o campo.
 */
export const ChecklistEntityTab: React.FC<ChecklistEntityTabProps> = ({ entityType, entityId, isOpen }) => {
  const gated = isOpen && !!entityId;

  const { data, isLoading } = useChecklistsByEntity(entityType, entityId, gated);
  const { data: userOptions } = useUsersDropdown({ enabled: gated });

  const userMap = useMemo(() => new Map((userOptions ?? []).map((u) => [u.id, u.name])), [userOptions]);
  const assigneeSelectOptions = useMemo<SelectOption[]>(
    () => (userOptions ?? []).map((u) => ({ value: u.id, label: u.name })),
    [userOptions],
  );

  // 404 do by-entity (user sem acesso ao pai — não deveria acontecer dentro do
  // próprio drawer, mas o backend valida mesmo assim) já vem silenciado pelo
  // `_handleErrorLocally` do `checklistsApi.byEntity` (sem toast vermelho);
  // aqui só tratamos como lista vazia — nenhuma UI de erro dedicada.
  const items = data ?? [];

  const [showApplyModal, setShowApplyModal] = useState(false);

  const openChecklist = (checklist: ChecklistResponse) => {
    // Nova aba: preserva o drawer (lead/ticket) aberto na aba original — mesmo
    // padrão de `ToolsTab.tsx` (abrir rota interna sem sair do contexto atual).
    window.open(`/checklists/${checklist.id}`, '_blank');
  };

  return (
    <div data-testid="entity-checklists-tab">
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <div>
          <h4
            className="text-xs font-bold uppercase"
            style={{ letterSpacing: '.04em', color: 'var(--theme-muted-text)', margin: 0 }}
          >
            Checklists/Formulários
          </h4>
          {!isLoading && (
            <div className="text-[13px] mt-0.5" style={{ color: 'var(--theme-muted-text)' }} data-testid="entity-checklists-count">
              {items.length === 1 ? '1 aplicação neste ' : `${items.length} aplicações neste `}{entityType === 'lead' ? 'lead' : 'chamado'}
            </div>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon="plus"
          onClick={() => setShowApplyModal(true)}
          data-testid="apply-template-btn"
        >
          Aplicar modelo
        </Button>
      </div>

      {isLoading && <ListPageLoading size="sm" />}

      {!isLoading && items.length === 0 && (
        <ListPageEmpty icon="check-square" message="Nenhum formulário ou checklist associado" />
      )}

      {!isLoading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((checklist) => {
            const meta = CHECKLIST_STATUS_META[checklist.status];
            const due = formatDueRelative(checklist.expiresAt);
            const isPending = checklist.status === 'pending' || checklist.status === 'in_progress';
            const kind = TEMPLATE_KIND_META[checklist.templateType] ?? TEMPLATE_KIND_META.checklist;
            const isDone = checklist.status === 'completed' || checklist.status === 'approved';
            const viaQr = checklist.assignmentContext?.strategy === 'qr_capture';
            // Chips de resposta só fazem sentido pro modelo tipo 'form' — um
            // checklist concluído com campo FormFieldType.PHOTO viraria chip
            // com a URL crua da foto (achado da revisão, fix1 #2).
            const chips = isDone && checklist.templateType === 'form'
              ? checklist.answers.map((a) => answerChip(checklist, a)).filter((c): c is string => c !== null)
              : [];
            const who = checklist.assigneeName ?? userMap.get(checklist.assigneeId) ?? 'Sem responsável';
            // Formulário gravado pelo nó "Salvar formulário" de um fluxo
            // (2026-09-28): quem respondeu foi o cliente, no WhatsApp — o
            // responsável é só quem o sistema registra.
            const viaFluxo = checklist.templateType === 'form' && checklist.assignmentContext?.strategy === 'workflow';
            // Contagem de campos com foto — mockup 13-A: "6 itens, 2 com foto"
            // (só anexa quando > 0). Fix1 #1.
            const photoFieldCount = checklist.templateSnapshot.fields.filter(
              (f) => f.type === FormFieldType.PHOTO,
            ).length;
            const itemsLabel = `${checklist.templateSnapshot.fields.length} itens${
              photoFieldCount > 0 ? `, ${photoFieldCount} com foto` : ''
            }`;
            // "via captura QR" fica FORA de `subParts` — é renderizada à parte
            // num nó visível próprio (com o data-testid), pra não duplicar a
            // leitura em screen reader (fix1 #3, substitui o antigo
            // `<span class="sr-only">` que repetia o texto já presente no sub).
            const subParts = isDone
              ? [viaFluxo ? 'Respondido pelo cliente (automação)' : `Respondido por ${who}`, formatWhen(checklist.completedAt)].filter(Boolean)
              : [`Responsável: ${who}`, due.label, itemsLabel].filter(Boolean);
            const sub = subParts.join(' · ');
            return (
              <div
                key={checklist.id}
                role="button"
                tabIndex={0}
                className="rounded-lg cursor-pointer transition-colors"
                style={{ border: '1px solid var(--theme-card-border)', padding: '10px 12px' }}
                onClick={() => openChecklist(checklist)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openChecklist(checklist);
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                data-testid={`entity-checklist-item-${checklist.id}`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-none"
                    style={{ backgroundColor: kind.bg, color: kind.fg }}
                    aria-hidden
                  >
                    <Icon name={kind.icon} size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
                        {checklist.templateSnapshot.name}
                      </span>
                      <span
                        className="text-[10px] font-bold uppercase rounded-[5px] px-1.5 py-0.5"
                        style={{
                          letterSpacing: '.05em',
                          backgroundColor: kind.kindBg,
                          color: kind.kindFg,
                        }}
                        data-testid={`entity-checklist-kind-${checklist.id}`}
                      >
                        {kind.label}
                      </span>
                      <StatusPill
                        variant={meta.variant}
                        label={isDone && checklist.templateType === 'form' ? 'Concluído' : meta.label}
                        data-testid={`entity-checklist-status-${checklist.id}`}
                      />
                    </div>
                    <div
                      className="text-xs mt-0.5"
                      style={{ color: isPending && due.overdue ? 'var(--theme-error-color)' : 'var(--theme-muted-text)' }}
                    >
                      {sub}
                      {viaQr && (
                        <>
                          {subParts.length > 0 ? ' · ' : ''}
                          <span data-testid={`entity-checklist-qr-${checklist.id}`}>via captura QR</span>
                        </>
                      )}
                    </div>
                    {chips.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2" data-testid={`entity-checklist-answers-${checklist.id}`}>
                        {chips.slice(0, MAX_ANSWER_CHIPS).map((chip, i) => (
                          <span
                            key={`${chip}-${i}`}
                            className="text-xs rounded-full"
                            style={{ border: '1px solid var(--theme-input-border)', color: 'var(--theme-card-text)', padding: '1px 8px' }}
                          >
                            {chip}
                          </span>
                        ))}
                        {chips.length > MAX_ANSWER_CHIPS && (
                          <span
                            className="text-xs rounded-full"
                            style={{ border: '1px solid var(--theme-input-border)', color: 'var(--theme-muted-text)', padding: '1px 8px' }}
                          >
                            +{chips.length - MAX_ANSWER_CHIPS}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className="flex-none text-[11px] mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                    {isDone ? 'ver respostas ›' : 'abrir ›'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <p className="text-xs mt-3" style={{ color: 'var(--theme-muted-text)' }}>
          Formulários e checklists usam a mesma base de modelos. O botão &quot;Aplicar modelo&quot; escolhe entre os dois.
        </p>
      )}

      <ApplyChecklistTemplateModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        linkedEntity={{ type: entityType, id: entityId }}
        assigneeOptions={assigneeSelectOptions}
      />
    </div>
  );
};

ChecklistEntityTab.displayName = 'ChecklistEntityTab';
